import React from 'react';
import {
  Alert,
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { Navigate, useNavigate } from 'react-router-dom';
import globals from '../globals';
import './index.css';

function formatBytes(bytes = 0) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function pathCrumbs(currentPath, username, onNavigate) {
  const parts = currentPath === '/' ? [] : currentPath.split('/').filter(Boolean);
  const crumbs = [{ label: username, path: '/' }];
  let pathCursor = '';
  parts.forEach((part) => {
    pathCursor = `${pathCursor}/${part}`;
    crumbs.push({ label: part, path: pathCursor });
  });
  return crumbs.map((crumb) => (
    <Link
      color="inherit"
      component="button"
      key={crumb.path}
      onClick={() => onNavigate(crumb.path)}
      underline="hover"
    >
      {crumb.label}
    </Link>
  ));
}

async function apiRequest(token, endpoint, body) {
  const response = await fetch(`${globals.api_root}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.id}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed: ${endpoint}`);
  return data;
}

const Home = ({ token, setToken }) => {
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = React.useState('/');
  const [folder, setFolder] = React.useState({ count: 0, items: [] });
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [shareLink, setShareLink] = React.useState('');
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuItem, setMenuItem] = React.useState(null);

  const loadDirectory = React.useCallback(async (dir, nextPage, nextRows) => {
    if (!token?.id) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await apiRequest(token, '/dir', {
        dir,
        page: nextPage,
        rows_per_page: nextRows,
      });
      setFolder(response.data || { count: 0, items: [] });
      setCurrentPath(dir);
      setPage(nextPage);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadDirectory('/', 0, rowsPerPage);
  }, [loadDirectory, rowsPerPage]);

  if (!token?.id) return <Navigate replace to="/login" />;

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const signOut = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    navigate('/login');
  };

  const openItem = async (item) => {
    if (item.type === 'folder') {
      loadDirectory(item.url_path, 0, rowsPerPage);
      return;
    }

    const response = await fetch(`${globals.api_root}/file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.id}`,
      },
      body: JSON.stringify({ id: item.id }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setError(body.error || 'Download failed');
      return;
    }
    const blob = await response.blob();
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
  };

  const addFolder = async () => {
    if (!newFolderName.trim()) return;
    const fullPath = currentPath === '/' ? `/${newFolderName}` : `${currentPath}/${newFolderName}`;
    setError('');
    try {
      await apiRequest(token, '/add-dir', { dir: fullPath });
      setNewFolderName('');
      loadDirectory(currentPath, page, rowsPerPage);
    } catch (folderError) {
      setError(folderError.message);
    }
  };

  const uploadFiles = async () => {
    if (!selectedFiles.length) return;
    setIsUploading(true);
    setError('');
    const body = new FormData();
    body.append('dir', currentPath);
    selectedFiles.forEach((file) => body.append('attachments', file));

    try {
      const response = await fetch(`${globals.upload_root}/uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token.id}` },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Upload failed');
      setSelectedFiles([]);
      loadDirectory(currentPath, page, rowsPerPage);
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Move ${item.name} to trash?`)) return;
    setError('');
    try {
      await apiRequest(token, '/trash', { fdpath: item.url_path, fdtype: item.type });
      loadDirectory(currentPath, page, rowsPerPage);
    } catch (trashError) {
      setError(trashError.message);
    }
  };

  const copyShareLink = async (item) => {
    try {
      const response = await apiRequest(token, '/sharelink', { id: item.id });
      setShareLink(response.link);
      await navigator.clipboard?.writeText(response.link);
    } catch (shareError) {
      setError(shareError.message);
    }
  };

  const totalBytes = folder.items.reduce((sum, item) => sum + Number(item.size || 0), 0);
  const folderCount = folder.items.filter((item) => item.type === 'folder').length;
  const fileCount = folder.items.filter((item) => item.type === 'file').length;

  return (
    <Box className="console-shell">
      <AppBar position="sticky" color="inherit" elevation={0} className="topbar">
        <Toolbar>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexGrow: 1 }}>
            <Box className="brand-mark brand-mark-small"><StorageOutlinedIcon /></Box>
            <Box>
              <Typography variant="h6">XOFTTEL</Typography>
              <Typography variant="caption" color="text.secondary">Recovery Cloud</Typography>
            </Box>
          </Stack>
          <Button color="inherit" onClick={signOut} startIcon={<LogoutIcon />}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" className="console-container">
        <Box className="console-header">
          <Box>
            <Typography variant="h4" component="h1">Backup Control Plane</Typography>
            <Typography color="text.secondary">
              Track remote OS/data images, manage recovery payloads, and prepare restore handoffs.
            </Typography>
          </Box>
          <Chip icon={<SecurityOutlinedIcon />} label="Offsite vault online" color="success" variant="outlined" />
        </Box>

        {error && <Alert severity="error" className="status-alert">{error}</Alert>}

        <Box className="metric-grid">
          <Paper className="metric-card" elevation={0}>
            <Typography variant="overline">Current vault</Typography>
            <Typography variant="h5">{token.username}</Typography>
            <Typography color="text.secondary">Authenticated backup namespace</Typography>
          </Paper>
          <Paper className="metric-card metric-card-blue" elevation={0}>
            <Typography variant="overline">Objects</Typography>
            <Typography variant="h5">{folder.count}</Typography>
            <Typography color="text.secondary">{folderCount} folders, {fileCount} files visible</Typography>
          </Paper>
          <Paper className="metric-card metric-card-teal" elevation={0}>
            <Typography variant="overline">Visible payload</Typography>
            <Typography variant="h5">{formatBytes(totalBytes)}</Typography>
            <Typography color="text.secondary">Current page only</Typography>
          </Paper>
          <Paper className="metric-card metric-card-amber" elevation={0}>
            <Typography variant="overline">Restore posture</Typography>
            <Typography variant="h5">Ready</Typography>
            <Typography color="text.secondary">Download or share payloads for recovery</Typography>
          </Paper>
        </Box>

        <Box className="workspace-grid">
          <Paper className="panel" elevation={0}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">Add backup set</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a namespace for a machine, disk, or recovery point.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  label="Folder or backup-set name"
                  onChange={(event) => setNewFolderName(event.target.value)}
                  size="small"
                  value={newFolderName}
                />
                <Button onClick={addFolder} startIcon={<AddIcon />} variant="contained">
                  Create
                </Button>
              </Stack>
              <Divider />
              <Box>
                <Typography variant="h6">Upload recovery payload</Typography>
                <Typography variant="body2" color="text.secondary">
                  Attach VM images, disk exports, manifests, or metadata files to the selected path.
                </Typography>
              </Box>
              <Button component="label" startIcon={<CloudUploadOutlinedIcon />} variant="outlined">
                Select files
                <input
                  hidden
                  multiple
                  onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
                  type="file"
                />
              </Button>
              {!!selectedFiles.length && (
                <Stack spacing={1}>
                  {selectedFiles.map((file) => (
                    <Chip key={`${file.name}-${file.size}`} label={`${file.name} - ${formatBytes(file.size)}`} />
                  ))}
                  <Button disabled={isUploading} onClick={uploadFiles} variant="contained">
                    {isUploading ? 'Uploading...' : 'Upload to vault'}
                  </Button>
                </Stack>
              )}
              {isUploading && <LinearProgress />}
            </Stack>
          </Paper>

          <Paper className="panel table-panel" elevation={0}>
            <Stack spacing={2}>
              <Box className="table-heading">
                <Box>
                  <Typography variant="h6">Vault Browser</Typography>
                  <Breadcrumbs>{pathCrumbs(currentPath, token.username, (pathValue) => loadDirectory(pathValue, 0, rowsPerPage))}</Breadcrumbs>
                </Box>
                <Button
                  onClick={() => loadDirectory(currentPath, page, rowsPerPage)}
                  startIcon={<RestoreOutlinedIcon />}
                  variant="outlined"
                >
                  Refresh
                </Button>
              </Box>

              {isLoading ? <LinearProgress /> : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Modified</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {folder.items.map((item) => (
                      <TableRow hover key={item.id}>
                        <TableCell>
                          <Button
                            color="inherit"
                            onClick={() => openItem(item)}
                            startIcon={item.type === 'folder' ? <FolderOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
                          >
                            {item.name || token.username}
                          </Button>
                        </TableCell>
                        <TableCell><Chip label={item.type} size="small" /></TableCell>
                        <TableCell align="right">{formatBytes(item.size)}</TableCell>
                        <TableCell>{item.modified ? new Date(item.modified).toLocaleString() : '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="More actions">
                            <IconButton
                              onClick={(event) => {
                                setMenuAnchor(event.currentTarget);
                                setMenuItem(item);
                              }}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!folder.items.length && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box className="empty-state">
                            <StorageOutlinedIcon />
                            <Typography variant="h6">No backup payloads here</Typography>
                            <Typography color="text.secondary">Create a backup set or upload a recovery image.</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              <TablePagination
                component="div"
                count={folder.count}
                onPageChange={(event, nextPage) => loadDirectory(currentPath, nextPage, rowsPerPage)}
                onRowsPerPageChange={(event) => {
                  const nextRows = parseInt(event.target.value, 10);
                  setRowsPerPage(nextRows);
                  loadDirectory(currentPath, 0, nextRows);
                }}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Stack>
          </Paper>
        </Box>
      </Container>

      <Menu anchorEl={menuAnchor} onClose={closeMenu} open={Boolean(menuAnchor)}>
        {menuItem?.type === 'file' && (
          <MenuItem onClick={() => { copyShareLink(menuItem); closeMenu(); }}>
            <ContentCopyOutlinedIcon fontSize="small" /> Copy restore link
          </MenuItem>
        )}
        <MenuItem onClick={() => { removeItem(menuItem); closeMenu(); }}>
          <DeleteOutlineIcon fontSize="small" /> Move to trash
        </MenuItem>
      </Menu>

      <Dialog onClose={() => setShareLink('')} open={Boolean(shareLink)}>
        <DialogTitle>Restore link copied</DialogTitle>
        <DialogContent>
          <Typography className="share-link">{shareLink}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareLink('')}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Home;
